import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import { workforceGuardrailRuleLabels } from "src/queries/workforceGuardrailRuleLabels.graphql";

import type {
  GuardrailCategory,
  GuardrailCheck,
  GuardrailEvaluation,
} from "./RequisitionGuardrailOutcome";

export type RuleLabel = {
  id: string;
  name?: string | null;
  description?: string | null;
  category?: GuardrailCategory | null;
  ownerRole?: string | null;
};

export type RuleLabelsData = {
  workforceGuardrailMonitor?: {
    rules?: Array<RuleLabel | null> | null;
  } | null;
};

export const ruleLabelsOf = (data: RuleLabelsData | undefined): RuleLabel[] =>
  (data?.workforceGuardrailMonitor?.rules ?? []).flatMap((rule) =>
    rule?.id ? [rule] : []
  );

export const withCheckLabels = (
  check: GuardrailCheck,
  label: {
    name?: string | null;
    description?: string | null;
    category?: GuardrailCategory | null;
    ownerRole?: string | null;
  }
): GuardrailCheck => {
  const name = label.name?.trim();
  const description = label.description?.trim();
  const ownerRole = label.ownerRole?.trim();
  return {
    ...check,
    ...(name ? { name } : {}),
    ...(description ? { description } : {}),
    ...(label.category ? { category: label.category } : {}),
    ...(ownerRole ? { ownerRole } : {}),
  };
};

export const useGuardrailRuleLabels = (
  enabled: boolean
): { rules: RuleLabel[]; ready: boolean } => {
  const { data, error } = useQuery<RuleLabelsData>(
    workforceGuardrailRuleLabels,
    {
      skip: !enabled,
      fetchPolicy: "cache-and-network",
    }
  );
  const rules = useMemo(() => ruleLabelsOf(data), [data]);
  const ready = !enabled || data != null || error != null;
  return { rules, ready };
};

/** Join monitor rule words onto a stamp. A missing rule keeps the check as stored. */
export const withRuleLabels = (
  evaluation: GuardrailEvaluation | null | undefined,
  rules: RuleLabel[]
): GuardrailEvaluation | null | undefined => {
  if (!evaluation || rules.length === 0) {
    return evaluation;
  }
  const byId = new Map(rules.map((rule) => [rule.id, rule]));
  return {
    ...evaluation,
    checks: evaluation.checks.map((check) => {
      const rule = byId.get(check.ruleId);
      if (!rule) {
        return check;
      }
      return withCheckLabels(check, rule);
    }),
  };
};
